import React, { useState, useEffect } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import {
  Container,
  Paper,
  Typography,
  Box,
  Button,
  Alert,
  CircularProgress,
} from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ErrorIcon from '@mui/icons-material/Error';
import axios from 'axios';
import { C } from '../theme';

const pageSx = {
  pt: '110px',
  pb: 8,
  minHeight: '100vh',
  backgroundColor: C.bg,
  backgroundImage: 'radial-gradient(ellipse 70% 50% at 50% -10%, rgba(243,192,21,0.10), transparent 70%)',
};
const paperSx = { p: { xs: 3, md: 5 }, backgroundColor: C.surface, borderRadius: 4, border: `1px solid ${C.line}`, textAlign: 'center' as const };

const VerifyEmail: React.FC = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [status, setStatus] = useState<'loading' | 'success' | 'error'>('loading');
  const [message, setMessage] = useState('');

  useEffect(() => {
    const verifyEmail = async () => {
      const token = searchParams.get('token');

      if (!token) {
        setStatus('error');
        setMessage('Verifikacioni token nije pronađen');
        return;
      }

      try {
        await axios.post('/api/auth/verify-email', { token });
        setStatus('success');
        setMessage('Vaša email adresa je uspešno potvrđena!');
      } catch (error: any) {
        setStatus('error');
        setMessage(error.response?.data?.message || 'Greška pri verifikaciji email adrese');
      }
    };

    verifyEmail();
  }, [searchParams]);

  const handleResendVerification = async () => {
    navigate('/login');
  };

  return (
    <Box sx={pageSx}>
      <Container maxWidth="sm">
        <Box sx={{ textAlign: 'center', mb: 5 }}>
          <Typography variant="h2" sx={{ fontWeight: 800, mb: 1.5, color: C.white, fontSize: { xs: '2rem', md: '2.6rem' } }}>
            Verifikacija email adrese
          </Typography>
        </Box>

        <Paper elevation={0} sx={paperSx}>
          {status === 'loading' && (
            <Box>
              <CircularProgress size={60} sx={{ mb: 3, color: C.gold }} />
              <Typography variant="h6" sx={{ color: C.muted }}>
                Verifikujem vašu email adresu...
              </Typography>
            </Box>
          )}

          {status === 'success' && (
            <Box>
              <CheckCircleIcon sx={{ fontSize: 80, color: C.success, mb: 3 }} />
              <Typography variant="h5" sx={{ mb: 2, color: C.white, fontWeight: 700 }}>
                Uspešna verifikacija!
              </Typography>
              <Typography variant="body1" sx={{ color: C.muted, mb: 4, lineHeight: 1.6 }}>
                {message}
              </Typography>
              <Button variant="contained" onClick={() => navigate('/login')} sx={{ px: 4, py: 1.5 }}>
                Prijavite se
              </Button>
            </Box>
          )}

          {status === 'error' && (
            <Box>
              <ErrorIcon sx={{ fontSize: 80, color: C.danger, mb: 3 }} />
              <Typography variant="h5" sx={{ mb: 2, color: C.white, fontWeight: 700 }}>
                Greška pri verifikaciji
              </Typography>
              <Alert severity="error" sx={{ mb: 4, textAlign: 'left' }}>
                {message}
              </Alert>
              <Box sx={{ display: 'flex', gap: 2, justifyContent: 'center', flexWrap: 'wrap' }}>
                <Button variant="outlined" onClick={() => navigate('/login')}>
                  Prijavite se
                </Button>
                <Button variant="contained" onClick={handleResendVerification}>
                  Ponovo pošaljite verifikaciju
                </Button>
              </Box>
            </Box>
          )}
        </Paper>
      </Container>
    </Box>
  );
};

export default VerifyEmail;
